import sortBy from 'lodash/sortBy';

import { DataType } from '@pressreader/feed-types';
import { loaded as resLoaded, localizedString } from '@pressreader/resources';
import {
    AnyCollectionFeedItem,
    CollectionItemOwner,
    CollectionPage,
    CollectionPagesFeedItemData,
    CollectionType,
    IUserCollection,
} from '@pressreader/social-types';
import { isPropertyOf, parseDateString } from '@pressreader/utils';

import { READ_LATER_COLLECTION_ID } from '../constants';
import type { AnyCollectionFeedItemDto, CollectionOwnerDto, CollectionPageDto, CollectionPagesDataDto, UserCollectionDto } from '../types';

const resourceNames = {
    facebook: 'Menu.Facebook',
    twitter: 'Menu.Twitter',
    evernote: 'Menu.Evernote',
    instapaper: 'Menu.Instapaper',
    onenote: 'Menu.OneNote',
    email: 'Menu.eMail',
    all: 'ReadLater',
    channel: 'Channel',
    public_all: 'ReadLater',
};

function mapCollectionType(t: number): CollectionType | undefined {
    switch (t) {
        case 1:
            return CollectionType.System;
        case 2:
            return CollectionType.User;
        case 3:
            return CollectionType.Sharing;
    }
    return undefined;
}

async function sortAndMapCollections(items: UserCollectionDto[]) {
    await resLoaded();

    // Put read later first and the rest sorted by collection names.
    return sortBy(items, item => (item.id === READ_LATER_COLLECTION_ID ? 0 : 1), 'name').map(mapUserCollection);
}

function mapUserCollection(item: UserCollectionDto): IUserCollection {
    let title = item.name;
    const id = item.id;
    if (item.type !== CollectionType.User && isPropertyOf(id, resourceNames)) {
        title = localizedString(resourceNames[id]);
    }
    const isDeleteDisabled = id === READ_LATER_COLLECTION_ID;
    return {
        id,
        displayName: title,
        name: title,
        slug: item.webName,
        isPublic: item.isPublic,
        type: mapCollectionType(item.type),
        contentCount: item.contentCount,
        isDeleteDisabled,
    };
}

function mapCollectionItemOwner(dto: CollectionOwnerDto): CollectionItemOwner {
    return {
        avatarImageId: dto.avatarImageId,
        enAccountNumber: dto.enAccountNumber,
        followedByCurrentUser: dto.followedByCurrentUser,
        nickname: dto.nickname,
        photoUrl: dto.photoUrl,
        postsCount: dto.postsCount,
        profileName: dto.profileName,
    };
}

function mapCollectionPage(dto: CollectionPageDto): CollectionPage {
    return {
        cid: dto.cid,
        issueDate: parseDateString(dto.issueDate) ?? new Date(dto.issueDate),
        issueId: dto.issueId,
        issueVersion: dto.issueVersion,
        pageName: dto.page,
        pageHeight: dto.pageHeight,
        pageNumber: dto.pageNumber,
        pageWidth: dto.pageWidth,
        publicationName: dto.publicationName,
        ticket: dto.ticket,
    };
}

function mapCollectionPagesFeedItemData(dto: CollectionPagesDataDto): CollectionPagesFeedItemData {
    return {
        bookmarkId: dto.bookmarkId,
        owner: mapCollectionItemOwner(dto.owner),
        pages: dto.pages.map(mapCollectionPage),
    };
}

function mapCollectionFeedItem(item: AnyCollectionFeedItemDto): AnyCollectionFeedItem | null {
    if (item.type === DataType.article) {
        return {
            id: item.id,
            type: item.type,
            isFeatured: item.isFeatured,
            data: item.data,
        };
    }
    if (item.type === DataType.bookmarkPages) {
        return {
            id: item.id,
            type: item.type,
            isFeatured: item.isFeatured,
            data: mapCollectionPagesFeedItemData(item.data),
        };
    }
    return null;
}

export { sortAndMapCollections, mapUserCollection, mapCollectionFeedItem };
